import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { contratoService } from '../services/api';
import { handleApiError } from '../utils/apiError';

const { Text } = Typography;

const STATUS_INTERNO_COLORS = {
  GERADO: 'blue',
  ENVIADO: 'gold',
  ASSINADO: 'green',
  CANCELADO: 'default',
  ERRO: 'red',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = dayjs(value);
  if (!date.isValid()) return '-';
  return date.format('DD/MM/YYYY HH:mm');
};

const ContratosList = () => {
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [acaoId, setAcaoId] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheContrato, setDetalheContrato] = useState(null);

  const fetchContratos = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) {
        params.status_interno = statusFilter;
      }
      const response = await contratoService.list(params);
      setContratos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      handleApiError(error, 'Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContratos();
  }, [statusFilter]);

  const statusOptions = useMemo(() => {
    const values = Array.from(new Set(contratos.map((c) => c?.status_interno || '-')));
    return values.filter(Boolean).sort().map((value) => ({ label: value, value }));
  }, [contratos]);

  const abrirDetalhes = async (id) => {
    setAcaoId(id);
    try {
      const response = await contratoService.get(id);
      setDetalheContrato(response.data || null);
      setDetalheOpen(true);
    } catch (error) {
      handleApiError(error, 'Erro ao carregar detalhes do contrato');
    } finally {
      setAcaoId(null);
    }
  };

  const sincronizar = async (id) => {
    setAcaoId(id);
    try {
      await contratoService.sincronizarStatus(id);
      message.success('Status sincronizado com sucesso');
      fetchContratos();
    } catch (error) {
      handleApiError(error, 'Erro ao sincronizar status do contrato');
    } finally {
      setAcaoId(null);
    }
  };

  const cancelar = async (record) => {
    Modal.confirm({
      title: `Cancelar contrato ${record.id}?`,
      content: 'Esta ação é permitida apenas para contratos não assinados.',
      okText: 'Cancelar contrato',
      cancelText: 'Voltar',
      okButtonProps: { danger: true },
      onOk: async () => {
        setAcaoId(record.id);
        try {
          await contratoService.cancelar(record.id);
          message.success('Contrato cancelado com sucesso');
          fetchContratos();
        } catch (error) {
          handleApiError(error, 'Erro ao cancelar contrato');
          throw error;
        } finally {
          setAcaoId(null);
        }
      },
    });
  };

  const excluir = async (record) => {
    Modal.confirm({
      title: `Excluir contrato ${record.id}?`,
      content: 'Esta ação remove apenas do banco da aplicação e não integra com a D4Sign.',
      okText: 'Excluir contrato',
      cancelText: 'Voltar',
      okButtonProps: { danger: true },
      onOk: async () => {
        setAcaoId(record.id);
        try {
          await contratoService.delete(record.id);
          message.success('Contrato excluído com sucesso');
          fetchContratos();
        } catch (error) {
          handleApiError(error, 'Erro ao excluir contrato');
          throw error;
        } finally {
          setAcaoId(null);
        }
      },
    });
  };

  const enviarParaAssinatura = async (record) => {
    Modal.confirm({
      title: `Enviar contrato ${record.id} para assinatura?`,
      content: 'O documento será enviado aos signatários na D4Sign.',
      okText: 'Enviar para assinatura',
      cancelText: 'Voltar',
      onOk: async () => {
        setAcaoId(record.id);
        try {
          await contratoService.enviarParaAssinatura(record.id);
          message.success('Contrato enviado para assinatura com sucesso');
          fetchContratos();
        } catch (error) {
          handleApiError(error, 'Erro ao enviar contrato para assinatura');
          throw error;
        } finally {
          setAcaoId(null);
        }
      },
    });
  };

  const copiarDadosContrato = async (contrato) => {
    const texto = `
Contrato #${contrato.id}
Aluno: ${contrato.aluno_nome}
Responsável: ${contrato.responsavel_nome}
Status interno: ${contrato.status_interno}
Status D4Sign: ${contrato.status_d4sign}
Documento: ${contrato.d4sign_document_uuid || contrato.d4sign_document_id || 'N/A'}
`;

    if (!navigator?.clipboard) {
      message.warning('Seu navegador não suporta cópia automática.');
      return;
    }

    try {
      await navigator.clipboard.writeText(texto);
      message.success('Dados copiados!');
    } catch (error) {
      handleApiError(error, 'Erro ao copiar dados do contrato');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
    },
    {
      title: 'Matrícula',
      dataIndex: 'id_matricula',
      key: 'id_matricula',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: 'Aluno',
      dataIndex: 'aluno_nome',
      key: 'aluno_nome',
      render: (value) => value || '-',
    },
    {
      title: 'Responsável',
      dataIndex: 'responsavel_nome',
      key: 'responsavel_nome',
      render: (value) => value || '-',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_documento',
      key: 'tipo_documento',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: 'Status Interno',
      dataIndex: 'status_interno',
      key: 'status_interno',
      width: 140,
      render: (value) => <Tag color={STATUS_INTERNO_COLORS[value] || 'default'}>{value || '-'}</Tag>,
    },
    {
      title: 'Status D4Sign',
      dataIndex: 'status_d4sign',
      key: 'status_d4sign',
      width: 140,
      render: (value) => value || '-',
    },
    {
      title: 'Data Geração',
      dataIndex: 'data_geracao',
      key: 'data_geracao',
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Data Assinatura',
      dataIndex: 'data_assinatura',
      key: 'data_assinatura',
      width: 170,
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 220,
      render: (_, record) => {
        const statusInterno = (record.status_interno || '').toUpperCase();
        const podeEnviarParaAssinatura = statusInterno === 'GERADO';
        const podeCancelar = statusInterno !== 'ASSINADO' && statusInterno !== 'CANCELADO';

        return (
          <Space>
            <Tooltip title="Ver detalhes">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => abrirDetalhes(record.id)}
                loading={acaoId === record.id}
              />
            </Tooltip>
            <Tooltip title="Sincronizar status">
              <Button
                icon={<ReloadOutlined />}
                size="small"
                onClick={() => sincronizar(record.id)}
                loading={acaoId === record.id}
              />
            </Tooltip>
            <Tooltip title={podeEnviarParaAssinatura ? 'Enviar para assinatura' : 'Disponível apenas para contratos GERADOS'}>
              <Button
                icon={<SendOutlined />}
                size="small"
                disabled={!podeEnviarParaAssinatura}
                onClick={() => enviarParaAssinatura(record)}
                loading={acaoId === record.id}
              />
            </Tooltip>
            <Tooltip title="Copiar dados do contrato">
              <Button
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copiarDadosContrato(record)}
              />
            </Tooltip>
            <Tooltip title={podeCancelar ? 'Cancelar contrato' : 'Contrato assinado/cancelado não pode ser cancelado'}>
              <Button
                icon={<StopOutlined />}
                size="small"
                danger
                disabled={!podeCancelar}
                onClick={() => cancelar(record)}
                loading={acaoId === record.id}
              />
            </Tooltip>
            <Tooltip title="Excluir contrato (apenas banco)">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={() => excluir(record)}
                loading={acaoId === record.id}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        title="Gestão de Contratos"
        extra={
          <Space>
            <Select
              allowClear
              placeholder="Filtrar status"
              style={{ width: 220 }}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
              options={statusOptions}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchContratos}>
              Atualizar
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={contratos}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={`Contrato #${detalheContrato?.id || ''}`}
        open={detalheOpen}
        onCancel={() => setDetalheOpen(false)}
        footer={null}
        width={760}
      >
        {detalheContrato ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text strong>Título</Text>
            <Text>{detalheContrato.titulo || '-'}</Text>
            <Text strong>Matrícula</Text>
            <Text>{detalheContrato.id_matricula || '-'}</Text>
            <Text strong>Aluno</Text>
            <Text>{detalheContrato.aluno_nome || '-'}</Text>
            <Text strong>Responsável</Text>
            <Text>{detalheContrato.responsavel_nome || '-'}</Text>
            <Text strong>Template</Text>
            <Text>{detalheContrato.template_id || '-'}</Text>
            <Text strong>Document UUID D4Sign</Text>
            <Text>{detalheContrato.d4sign_document_uuid || detalheContrato.d4sign_document_id || '-'}</Text>
            <Text strong>Signer Key D4Sign</Text>
            <Text>{detalheContrato.d4sign_signer_key || '-'}</Text>
            <Text strong>Status interno / D4Sign</Text>
            <Text>{(detalheContrato.status_interno || '-') + ' / ' + (detalheContrato.status_d4sign || '-')}</Text>
          </Space>
        ) : null}
      </Modal>
    </Space>
  );
};

export default ContratosList;
